/**
 * History page - Bot task history, queue and live processing view
 */

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { collection, query, orderBy, limit, onSnapshot, where, doc, writeBatch } from 'firebase/firestore';
import { db } from '../services/firebase';
import { wywolajFunkcje } from '../services/funkcje';
import { useBotStatus } from '../hooks/useOrders';
import { Order, LogEntry, toDate } from '../types';
import '../styles/History.css';

export function History() {
  const { botStatus } = useBotStatus();
  const [recentOrders, setRecentOrders] = useState<Order[]>([]);
  const [selectedTasks, setSelectedTasks] = useState<Set<string>>(new Set());
  const [currentOrderLogs, setCurrentOrderLogs] = useState<LogEntry[]>([]);
  const [filter, setFilter] = useState<'all' | 'completed' | 'failed' | 'pending'>('all');
  const [isClearing, setIsClearing] = useState(false);
  const [isProcessingQueue, setIsProcessingQueue] = useState(false);
  const [queueDisplayLimit, setQueueDisplayLimit] = useState(10);
  const [isAddingToQueue, setIsAddingToQueue] = useState(false);
  
  // Real-time listener for recent orders (processed by bot)
  useEffect(() => {
    const ordersRef = collection(db, 'orders');
    const q = query(
      ordersRef,
      orderBy('updated_at', 'desc'),
      limit(50)
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const orders = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      })) as Order[];
      setRecentOrders(orders);
    });

    return () => unsubscribe();
  }, []);

  // Real-time listener for current order's logs
  useEffect(() => {
    if (!botStatus?.current_order_id) {
      setCurrentOrderLogs([]);
      return;
    }

    const logsRef = collection(db, 'logs');
    const q = query(
      logsRef,
      where('order_id', '==', botStatus.current_order_id),
      orderBy('timestamp', 'desc'),
      limit(20)
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const logs = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      })) as LogEntry[];
      setCurrentOrderLogs(logs);
    });

    return () => unsubscribe();
  }, [botStatus?.current_order_id]);

  // Filter orders based on selection
  const filteredOrders = recentOrders.filter(order => {
    if (filter === 'all') return true;
    if (filter === 'completed') return order.status === 'completed';
    if (filter === 'failed') return order.status === 'failed' || order.status === 'partial';
    if (filter === 'pending') return order.status === 'pending' || order.status === 'processing';
    return true;
  });

  // Get pending queue (orders waiting to be processed)
  const pendingQueue = recentOrders.filter(o => 
    o.status === 'pending' && !o.supplier_order_id
  );

  const handleSelectTask = (orderId: string, checked: boolean) => {
    setSelectedTasks(prev => {
      const newSet = new Set(prev);
      if (checked) newSet.add(orderId);
      else newSet.delete(orderId);
      return newSet;
    });
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedTasks(new Set(filteredOrders.map(o => o.id)));
    } else {
      setSelectedTasks(new Set());
    }
  };

  const handleRetrySelected = async () => {
    if (selectedTasks.size === 0) return;
    setIsAddingToQueue(true);
    try {
      const orderIds = Array.from(selectedTasks);
      const response = await wywolajFunkcje('processOrdersManually', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ orderIds }),
      });
      const data = await response.json();
      if (data.success) {
        alert(`✅ Dodano ${orderIds.length} zamówień do przetwarzania`);
        setSelectedTasks(new Set());
      } else {
        alert(`❌ Błąd: ${data.error || 'Nieznany błąd'}`);
      }
    } catch (error) {
      console.error('Error processing orders:', error);
      alert('❌ Błąd podczas uruchamiania przetwarzania');
    } finally {
      setIsAddingToQueue(false);
    }
  };

  const handleProcessQueue = async () => {
    if (pendingQueue.length === 0) return;
    
    const confirmed = window.confirm(
      `Czy chcesz uruchomić przetwarzanie ${pendingQueue.length} zamówień z kolejki?`
    );
    
    if (!confirmed) return;
    
    setIsProcessingQueue(true);
    try {
      const orderIds = pendingQueue.map(o => o.id);
      const response = await wywolajFunkcje('processOrdersManually', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ orderIds }),
      });
      const data = await response.json();
      if (data.success) {
        alert(`✅ Uruchomiono przetwarzanie ${orderIds.length} zamówień`);
      } else {
        alert(`❌ Błąd: ${data.error || 'Nieznany błąd'}`);
      }
    } catch (error) {
      console.error('Error processing queue:', error);
      alert('❌ Błąd podczas uruchamiania kolejki');
    } finally {
      setIsProcessingQueue(false);
    }
  };

  const handleAddSingleToQueue = async (orderId: string) => {
    try {
      const response = await wywolajFunkcje('processOrdersManually', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ orderIds: [orderId] }),
      });
      const data = await response.json();
      if (data.success) {
        alert('✅ Dodano zamówienie do przetwarzania');
      } else {
        alert(`❌ Błąd: ${data.error || 'Nieznany błąd'}`);
      }
    } catch (error) {
      console.error('Error adding to queue:', error);
      alert('❌ Błąd podczas dodawania do kolejki');
    }
  };

  const handleExpandQueue = () => {
    setQueueDisplayLimit(prev => prev + 25);
  };

  const handleClearQueue = async () => {
    if (pendingQueue.length === 0) return;
    
    const confirmed = window.confirm(
      `Czy na pewno chcesz usunąć ${pendingQueue.length} zamówień z kolejki?\n\nTa operacja jest nieodwracalna!`
    );
    
    if (!confirmed) return;
    
    setIsClearing(true);
    try {
      const batch = writeBatch(db);
      pendingQueue.forEach(order => {
        const orderRef = doc(db, 'orders', order.id);
        batch.delete(orderRef);
      });
      await batch.commit();
      alert(`✅ Usunięto ${pendingQueue.length} zamówień z kolejki`);
    } catch (error) {
      console.error('Error clearing queue:', error);
      alert('❌ Błąd podczas czyszczenia kolejki');
    } finally {
      setIsClearing(false);
    }
  };

  const getStatusBadge = (order: Order) => {
    const statusConfig: Record<string, { emoji: string; label: string; className: string }> = {
      pending: { emoji: '⏳', label: 'Oczekuje', className: 'status-pending' },
      processing: { emoji: '⚙️', label: 'Przetwarzane', className: 'status-processing' },
      completed: { emoji: '✅', label: 'Zakończone', className: 'status-completed' },
      failed: { emoji: '❌', label: 'Błąd', className: 'status-failed' },
      partial: { emoji: '⚠️', label: 'Częściowe', className: 'status-partial' },
      cancelled: { emoji: '🚫', label: 'Anulowane', className: 'status-cancelled' },
    };
    const config = statusConfig[order.status] || statusConfig.pending;
    return (
      <span className={`history-status-badge ${config.className}`}>
        {config.emoji} {config.label}
      </span>
    );
  };

  const formatDuration = (start?: Date | { seconds: number }, end?: Date | { seconds: number }) => {
    if (!start) return '-';
    const startDate = start instanceof Date ? start : new Date((start as any).seconds * 1000);
    const endDate = end ? (end instanceof Date ? end : new Date((end as any).seconds * 1000)) : new Date();
    const diffMs = endDate.getTime() - startDate.getTime();
    const seconds = Math.floor(diffMs / 1000);
    const minutes = Math.floor(seconds / 60);
    if (minutes > 0) return `${minutes}m ${seconds % 60}s`;
    return `${seconds}s`;
  };

  return (
    <div className="history-page">
      <div className="history-header">
        <h1>📜 Historia zadań bota</h1>
        <p className="history-subtitle">Podgląd na żywo, kolejka i historia przetwarzania</p>
      </div>

      {/* Live Processing Section */}
      {botStatus?.state === 'processing' && (
        <div className="live-processing-section">
          <div className="live-header">
            <div className="live-indicator">
              <span className="live-dot"></span>
              <span>Na żywo</span>
            </div>
            <h2>🤖 Aktualnie przetwarzane</h2>
          </div>

          <div className="live-content">
            {/* Left: Current order info */}
            <div className="live-order-info">
              <div className="live-order-id">
                <span className="label">Zamówienie:</span>
                <Link to={`/orders/${botStatus.current_order_id}`} className="order-link">
                  #{botStatus.woocommerce_order_id}
                </Link>
              </div>
              
              <div className="live-step">
                <span className="label">Krok:</span>
                <span className="value">
                  {botStatus.step_number}/{botStatus.total_steps || '?'} - {botStatus.current_step || 'Ładowanie...'}
                </span>
              </div>

              {botStatus.current_product && (
                <div className="live-product">
                  <span className="label">Produkt:</span>
                  <span className="value">{botStatus.current_product}</span>
                </div>
              )}

              {botStatus.items_processed !== undefined && (
                <div className="live-progress">
                  <span className="label">Produkty:</span>
                  <span className="value">{botStatus.items_processed}/{botStatus.items_total || '?'}</span>
                  <div className="progress-bar">
                    <div 
                      className="progress-fill"
                      style={{ width: `${(botStatus.items_processed / (botStatus.items_total || 1)) * 100}%` }}
                    />
                  </div>
                </div>
              )}

              {/* Recent logs */}
              <div className="live-logs">
                <h4>📋 Ostatnie akcje:</h4>
                <div className="logs-list">
                  {currentOrderLogs.slice(0, 5).map(log => (
                    <div key={log.id} className={`log-entry log-${log.level}`}>
                      <span className="log-time">
                        {toDate(log.timestamp).toLocaleTimeString()}
                      </span>
                      <span className="log-message">{log.message}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

          </div>
        </div>
      )}

      {/* Queue Section */}
      {pendingQueue.length > 0 && (
        <div className="queue-section">
          <div className="queue-header">
            <h2>📋 Kolejka ({pendingQueue.length})</h2>
            <div className="queue-actions">
              <button 
                className="process-queue-btn"
                onClick={handleProcessQueue}
                disabled={isProcessingQueue || botStatus?.state === 'processing'}
              >
                {isProcessingQueue ? '⏳ Uruchamianie...' : '▶️ Uruchom przetwarzanie'}
              </button>
              <button 
                className="clear-queue-btn"
                onClick={handleClearQueue}
                disabled={isClearing}
              >
                {isClearing ? '⏳ Czyszczenie...' : '🗑️ Wyczyść kolejkę'}
              </button>
            </div>
          </div>
          <div className="queue-list">
            {pendingQueue.slice(0, queueDisplayLimit).map((order, index) => (
              <div key={order.id} className="queue-item">
                <span className="queue-position">{index + 1}</span>
                <Link to={`/orders/${order.id}`} className="queue-order-link">
                  #{order.woocommerce_order_id}
                </Link>
                <span className="queue-customer">
                  {order.customer.first_name} {order.customer.last_name}
                </span>
                <span className="queue-items">{order.items.length} produktów</span>
              </div>
            ))}
            {pendingQueue.length > queueDisplayLimit && (
              <button 
                className="queue-more-btn"
                onClick={handleExpandQueue}
              >
                📋 ...i {pendingQueue.length - queueDisplayLimit} więcej (kliknij aby rozwinąć)
              </button>
            )}
          </div>
        </div>
      )}

      {/* History Section */}
      <div className="history-section">
        <div className="history-section-header">
          <h2>📜 Historia przetwarzania</h2>
          
          <div className="history-controls">
            <div className="filter-tabs">
              <button 
                className={filter === 'all' ? 'active' : ''}
                onClick={() => setFilter('all')}
              >
                Wszystkie ({recentOrders.length})
              </button>
              <button 
                className={filter === 'completed' ? 'active' : ''}
                onClick={() => setFilter('completed')}
              >
                ✅ Zakończone ({recentOrders.filter(o => o.status === 'completed').length})
              </button>
              <button 
                className={filter === 'failed' ? 'active' : ''}
                onClick={() => setFilter('failed')}
              >
                ❌ Błędy ({recentOrders.filter(o => o.status === 'failed' || o.status === 'partial').length})
              </button>
              <button 
                className={filter === 'pending' ? 'active' : ''}
                onClick={() => setFilter('pending')}
              >
                ⏳ Oczekujące ({recentOrders.filter(o => o.status === 'pending').length})
              </button>
            </div>

            {selectedTasks.size > 0 && (
              <div className="bulk-actions">
                <span>{selectedTasks.size} zaznaczonych</span>
                <button 
                  onClick={handleRetrySelected} 
                  className="retry-btn"
                  disabled={isAddingToQueue}
                >
                  {isAddingToQueue ? '⏳ Dodawanie...' : '🔄 Przetwórz zaznaczone'}
                </button>
                <button onClick={() => setSelectedTasks(new Set())} className="clear-btn">
                  ✕ Wyczyść
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="history-table-container">
          <table className="history-table">
            <thead>
              <tr>
                <th className="checkbox-col">
                  <input 
                    type="checkbox"
                    onChange={(e) => handleSelectAll(e.target.checked)}
                    checked={selectedTasks.size === filteredOrders.length && filteredOrders.length > 0}
                  />
                </th>
                <th>Zamówienie</th>
                <th>Status</th>
                <th>Klient</th>
                <th>Produkty</th>
                <th>Supplier ID</th>
                <th>Czas trwania</th>
                <th>Data</th>
                <th>Akcje</th>
              </tr>
            </thead>
            <tbody>
              {filteredOrders.map(order => (
                <tr key={order.id} className={selectedTasks.has(order.id) ? 'selected' : ''}>
                  <td className="checkbox-col">
                    <input 
                      type="checkbox"
                      checked={selectedTasks.has(order.id)}
                      onChange={(e) => handleSelectTask(order.id, e.target.checked)}
                    />
                  </td>
                  <td>
                    <Link to={`/orders/${order.id}`} className="order-id-link">
                      #{order.woocommerce_order_id}
                    </Link>
                  </td>
                  <td>{getStatusBadge(order)}</td>
                  <td>
                    <span className="customer-name">
                      {order.customer.first_name} {order.customer.last_name}
                    </span>
                  </td>
                  <td>
                    <span className="items-count">{order.items.length}</span>
                    {order.failed_items.length > 0 && (
                      <span className="failed-count">({order.failed_items.length} ❌)</span>
                    )}
                  </td>
                  <td>
                    {order.supplier_order_id ? (
                      <span className="supplier-id">{order.supplier_order_id}</span>
                    ) : (
                      <span className="no-supplier">-</span>
                    )}
                  </td>
                  <td>
                    {formatDuration(order.processing_started_at, order.processing_completed_at)}
                  </td>
                  <td>
                    <span className="date-cell">
                      {toDate(order.updated_at).toLocaleDateString('pl-PL')}
                      <br />
                      <span className="time">{toDate(order.updated_at).toLocaleTimeString('pl-PL')}</span>
                    </span>
                  </td>
                  <td className="actions-cell">
                    <Link to={`/orders/${order.id}`} className="action-btn view" title="Zobacz szczegóły">
                      👁️
                    </Link>
                    {(order.status === 'failed' || order.status === 'partial') && (
                      <button 
                        className="action-btn retry" 
                        onClick={() => handleAddSingleToQueue(order.id)}
                        title="Ponów przetwarzanie"
                      >
                        🔄
                      </button>
                    )}
                    {order.status === 'pending' && !order.supplier_order_id && (
                      <button 
                        className="action-btn queue" 
                        onClick={() => handleAddSingleToQueue(order.id)}
                        title="Uruchom przetwarzanie"
                      >
                        ▶️
                      </button>
                    )}
                    {order.status === 'completed' && (
                      <button 
                        className="action-btn add-queue" 
                        onClick={() => handleAddSingleToQueue(order.id)}
                        title="Dodaj do kolejki"
                      >
                        ➕
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {filteredOrders.length === 0 && (
            <div className="empty-state">
              <span>📭</span>
              <p>Brak zadań do wyświetlenia</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
